import { constants } from "@qgiv/core-js";
import { determineRegRolesFromSelectedCategoryId } from "@qgiv/p2p-registration";

const getCategoryInitialValue = (settings) => {
    // We should be looking at the filtered category list
    const { categoriesFilteredBySettingsAndUserSelections } = settings;
    const shouldAutoSelectOnlyCategory =
        categoriesFilteredBySettingsAndUserSelections.length === 1;
    const Category = shouldAutoSelectOnlyCategory
        ? [`${categoriesFilteredBySettingsAndUserSelections[0].id}`]
        : [];
    return {
        Category,
    };
};

const getRegistrationRoleInitialValue = (
    settings,
    primaryRegistrationValues,
    allowSubRegistrantToChangeRegRole,
) => {
    const {
        ENUMS: {
            EntityType: { REGISTRATION },
        },
    } = constants;
    const {
        categories,
        p2pSettings: {
            enableIndividualRegistration,
            enableTeams,
            enableTeamCreation,
            enableTeamJoin,
        },
        categoriesFilteredBySettingsAndUserSelections,
    } = settings;

    const shouldAutoSelectOnlyCategory =
        categoriesFilteredBySettingsAndUserSelections.length === 1;

    const primaryRegistrationRole =
        primaryRegistrationValues &&
        primaryRegistrationValues.Registration_Role &&
        primaryRegistrationValues.Registration_Role[0];

    const { isRegRoleIndividualOnly } = determineRegRolesFromSelectedCategoryId(
        {
            selectedCategoryId: shouldAutoSelectOnlyCategory
                ? Number(categoriesFilteredBySettingsAndUserSelections[0]?.id)
                : "",
            categories: categoriesFilteredBySettingsAndUserSelections,
            enableTeams,
            enableIndividualRegistration,
        },
    );

    const onlyOneCategoryWithIndividualOnly =
        shouldAutoSelectOnlyCategory && isRegRoleIndividualOnly;

    const teamsAreDisabled =
        !enableTeams || !!(!enableTeamCreation && !enableTeamJoin);

    const autoSelectIndividual =
        enableIndividualRegistration &&
        !!(teamsAreDisabled || onlyOneCategoryWithIndividualOnly);

    let Registration_Role = [];

    // If this is an Additional Registrant and they are not allowed to have
    // different roles than the PRimary then we should set the Role to the Primaries
    if (primaryRegistrationRole && !allowSubRegistrantToChangeRegRole) {
        Registration_Role = [primaryRegistrationRole];
    }

    // If the Additional Reg can have a different role then primary and there is only
    // one Category available with only Individual then we should set the role to
    // REGISTRATION
    if (allowSubRegistrantToChangeRegRole && autoSelectIndividual) {
        Registration_Role = [`${REGISTRATION}`];
    }

    return {
        Registration_Role,
    };
};

const getIsNfpInitialValue = () => {
    let Is_Nfp = false;
    return {
        Is_Nfp,
    };
};

const getManageByPrimaryInitialValue = () => {
    let Managed_By_Primary = false;
    return {
        Managed_By_Primary,
    };
};

const getParticipantDetailsInitialValues = (settings) => {
    const {
        formSettings: { defaultCountry },
    } = settings;

    const defaultValues = {
        First_Name: "",
        Last_Name: "",
        Email: "",
        Address_1: "",
        Address_2: "",
        City: "",
        State: "",
        Zip: "",
        Country: defaultCountry ? defaultCountry : "US",
        Phone: "",
        // Hidden field used to store the error that is thrown if an email has
        // already been during the current registration
        Verified_Email_Is_Unused_By_Current_Registration: false,
        // Hidden field used to store the error that is thrown if an email has
        // already been used during a previous registration
        Verified_Email_Is_Unused_By_Previous_Registration: false,
    };

    return {
        ...defaultValues,
    };
};

const getEmergencyContactValues = (suffix = "") => {
    const emergencyContactFields = [
        "Emergency_Contact_First_Name",
        "Emergency_Contact_Last_Name",
        "Emergency_Contact_Email",
        "Emergency_Contact_Phone",
        "Emergency_Contact_Cell",
    ];

    const buildEmergencyValues = emergencyContactFields.reduce(
        (newValues, key) => {
            if (suffix) {
                newValues[`${key}_${suffix}`] = "";
            } else {
                newValues[key] = "";
            }
            return newValues;
        },
        {},
    );

    return {
        ...buildEmergencyValues,
    };
};

const getAdditionalInfoFieldValues = (settings) => {
    const { fields } = settings;

    return fields.reduce((values, field) => {
        const { id } = field;
        return {
            ...values,
            [`Custom_Fields_${id}`]: "",
        };
    }, {});
};

const getTeamInitialValue = (settings) => {
    const {
        p2pSettings: { defaultTeamFundraisingGoal },
    } = settings;
    return {
        Team: "",
        Team_Captain: false,
        New_Team_Name: "",
        New_Team_Fundraising_Goal:
            defaultTeamFundraisingGoal > 0
                ? String(defaultTeamFundraisingGoal)
                : "",
    };
};

const getParticipantFundraisingGoalValues = (settings) => {
    const {
        p2pSettings: { defaultFundraisingGoal, enableCommitments },
        categories,
    } = settings;
    let isSingleCategoryWithCommitment =
        categories.length === 1 && categories[0].commitmentAmount > 0;
    let commitmentAmount;
    // If no default personal goal or commitment has been set up, the fundraising goal
    // field should be empty instead of having a 0 in it when the UI loads
    let fundraisingGoalInitialValue = "";

    // if single category and category has commitments always default to
    // the commitment amount
    if (isSingleCategoryWithCommitment && enableCommitments) {
        commitmentAmount = categories[0].commitmentAmount;
        fundraisingGoalInitialValue = String(commitmentAmount);
    }
    // if default personal goal set and there are no commitments
    // use the default fundraising goal
    if (!isSingleCategoryWithCommitment && defaultFundraisingGoal > 0) {
        fundraisingGoalInitialValue = String(defaultFundraisingGoal);
    }

    return {
        Participant_Fundraising_Goal: fundraisingGoalInitialValue,
    };
};

export const getInitialValues = (
    settings,
    savedRegistrationValues = {},
    primaryRegistrationValues = {},
    allowSubRegistrantToChangeRegRole,
) => {
    const values = {
        ...getCategoryInitialValue(settings),
        ...getRegistrationRoleInitialValue(
            settings,
            primaryRegistrationValues,
            allowSubRegistrantToChangeRegRole,
        ),
        ...getAdditionalInfoFieldValues(settings),
        ...getTeamInitialValue(settings),
        // Fundraising Goal fields
        ...getParticipantFundraisingGoalValues(settings),
        ...getManageByPrimaryInitialValue(),
        ...getIsNfpInitialValue(),
        // Participant Details fields
        ...getParticipantDetailsInitialValues(settings),
        // Emergency Contact Fields
        ...getEmergencyContactValues(),
        ...getEmergencyContactValues("2"),
        // Waiver Checkbox
        Accept_Waiver: false,
        // These are only use for sub-registrants
        Use_Primary_Address: false,
        Use_Primary_Phone: false,
        // Hidden fields used to confirm total amount based on Promo Codes used
        Original_Category_Fee: "",
        Post_Promo_Category_Fee: "",
        Promo_Codes: [],
        // This is a flag we use to track whether a user has confirmed pre-filled
        // Registration data. It is not set by formik, but rather, in our
        // `prepareValuesToSave` func.
        Needs_To_Be_Reviewed: true,
        // This is a flag we use to track whether a user has saved
        // Registration data by clicking Next/Add Additional. It is not set by formik, but rather, in our
        // `prepareValuesToSave` func. Used to determine if cancel button should delete changes
        Has_Been_Saved: false,
    };
    if (Object.keys(savedRegistrationValues).length > 0) {
        return {
            ...values,
            ...savedRegistrationValues,
        };
    }

    return values;
};
